import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Form,
  Icon,
  ICONS,
  Button,
  TextInput,
  Typography,
  SelectInput,
  ButtonsWrapper,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';
import { useTranslation } from 'react-i18next';

import { useRouteQuery } from 'src/common/helpers';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import { useUpdatePermissionGroup } from 'src/hooks/permission-group/update-permission-group';
import { useGetPermissionGroupById } from 'src/hooks/permission-group/get-permission-group-by-id.hook';
import { IPermissionDoc } from 'src/pages/settings/components/permission-group/components/modal-form/modal-form.interfaces';
import { PLAN_WERK_PERMISSIONS } from 'src/pages/settings/components/permission-group/components/modal-form/modal-form.constants';
import { PLATFORM_RULES_VALUES } from 'src/pages/settings/components/permission-group/components/modal-form/components/rules-list-select/rules-list-select-constants';

import {
  FormListRule,
  MainContainer,
  ButtonContainer,
  FormListContent,
  ContentSeparator,
  FormListPermission,
  FormListElementWrapper,
} from './edit-permission-group.styles';

const EditPermissionGroup = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation(['common', 'settings']);
  const navigate = useNavigate();
  const query = useRouteQuery();
  const permissionGroupId: string = query.get('id') || '';
  const data = useGetPermissionGroupById(permissionGroupId);
  const navigateToList = () =>
    navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.permissionGroups}`);
  const updateGroup = useUpdatePermissionGroup({
    onSuccess: () => {
      navigateToList();
      data.refetch();
    },
  });

  useEffect(() => form.setFieldsValue(data), [form, data]);

  return (
    <MainContainer>
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {data.name}
      </Typography>
      <Form
        layout="vertical"
        form={form}
        onFinish={(formData: IPermissionDoc) => updateGroup(permissionGroupId, formData)}
        initialValues={data}
      >
        <Form.Item
          rules={[
            { required: true, message: t('settings:permissionGroupSettings.errors.nameRequired') },
          ]}
          name="name"
          label={t('settings:permissionGroupSettings.labels.title')}
        >
          <TextInput />
        </Form.Item>
        <ContentSeparator />
        <Form.List name="permissions">
          {(fields, { add, remove }) => (
            <FormListContent>
              {fields.map((field, index) => (
                <FormListElementWrapper key={field.key}>
                  <FormListPermission>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currValues) =>
                        prevValues.permissions !== currValues.permissions
                      }
                    >
                      {({ getFieldValue }) => {
                        const permissions = getFieldValue('permissions') || [];
                        const selectedPermissions = permissions.map(
                          ({ permission }: { permission?: string } = {}) => permission
                        );

                        return (
                          <Form.Item
                            name={[field.name, 'permission']}
                            rules={[
                              {
                                required: true,
                                message: t(
                                  'settings:permissionGroupSettings.errors.permissionRequired'
                                ),
                              },
                            ]}
                            label={t('settings:permissionGroupSettings.labels.availablePages')}
                          >
                            <SelectInput
                              placeholder={t(
                                'settings:permissionGroupSettings.placeholders.selectNeededPage'
                              )}
                            >
                              {PLAN_WERK_PERMISSIONS.map(permission => (
                                <SelectInput.Option
                                  key={permission}
                                  value={permission}
                                  disabled={selectedPermissions.includes(permission)}
                                >
                                  {t(
                                    `settings:permissionGroupSettings.planWerkPermissions.${permission}`
                                  )}
                                </SelectInput.Option>
                              ))}
                            </SelectInput>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    {fields.length !== 1 ? (
                      <Icon
                        icon={ICONS.trash}
                        rightMargin={false}
                        color={PLATFORM_THEME_COLORS.tone.red}
                        size={22}
                        onClick={() => remove(field.name)}
                      />
                    ) : (
                      <Icon
                        icon={ICONS.trash}
                        rightMargin={false}
                        size={22}
                        color={PLATFORM_THEME_COLORS.grayVariations.medium}
                      />
                    )}
                  </FormListPermission>
                  <FormListRule>
                    <Form.Item
                      name={[field.name, 'rule']}
                      rules={[
                        {
                          required: true,
                          message: t('settings:permissionGroupSettings.errors.ruleRequired'),
                        },
                      ]}
                      label={t('settings:permissionGroupSettings.labels.typeOfRights')}
                    >
                      <SelectInput
                        placeholder={t('settings:permissionGroupSettings.labels.selectRights')}
                      >
                        {PLATFORM_RULES_VALUES.map(rule => (
                          <SelectInput.Option key={rule} value={rule}>
                            {t(`settings:permissionGroupSettings.rules.${rule}`)}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                    {index === fields.length - 1 && fields.length !== PLAN_WERK_PERMISSIONS.length && (
                      <ButtonContainer>
                        <Button
                          type="text"
                          icon={ICONS.plus}
                          iconColor={PLATFORM_THEME_COLORS.primary.blue}
                          onClick={add}
                        >
                          {t('settings:permissionGroupSettings.labels.addAvailablePages')}
                        </Button>
                      </ButtonContainer>
                    )}
                  </FormListRule>
                </FormListElementWrapper>
              ))}
            </FormListContent>
          )}
        </Form.List>

        <ButtonsWrapper>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={navigateToList}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </MainContainer>
  );
};

export default EditPermissionGroup;
